import { ArrowBack, Business, Email, Info, LocationOn, Phone } from '@mui/icons-material';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Container } from '@src/App/Components/Core/Layouts/LayoutElements/Container/Container';
import Content from '@src/App/Components/Core/Layouts/LayoutElements/Content/Content';
import ContentHeader from '@src/App/Components/Core/Layouts/LayoutElements/ContentHeader/ContentHeader';
import { isLoggedIn } from '@store/reducer/clientReducer';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

function Impressum()
{
    const theme = useTheme();
    const secret = useSelector((state: AppState) => state.client.secret);
    const loggedIn = isLoggedIn(secret);

    return (
        <Content contentHeader={<ContentHeader title="Impressum" />}>
            <Container>
                <Paper
                    elevation={0}
                    sx={{
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: 3,
                        p: 4,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        },
                    }}
                >
                    <Typography
                        component="button"
                        onClick={() => window.history.back()}
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            textDecoration: 'none',
                            fontSize: '0.75rem',
                            mb: 2,
                            display: 'block',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            textAlign: 'left',
                            '&:hover': {
                                color: 'primary.main',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        ← Zurück
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                            }}
                        >
                            <Info />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            Rechtliche Informationen
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Business sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    Angaben gemäß § 5 TMG
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Talentillion GmbH
                                    <br />
                                    Kronenstr. 18
                                    <br />
                                    10117 Berlin
                                    <br />
                                    Deutschland
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Email sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    Kontakt
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Telefon: +49-30-959998170
                                    <br />
                                    E-Mail: inbox@talentillion.de
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Business sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    Registereintrag
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Vertreten durch die Geschäftsführer:
                                    <br />
                                    Sebastian Ullrich, Andreas Sass
                                    <br />
                                    <br />
                                    Eingetragen beim Amtsgericht Charlottenburg
                                    <br />
                                    HRB 278450 B
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Business sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    Umsatzsteuer-ID
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:
                                    <br />
                                    UStIdNr.: XXX
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <LocationOn sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Sebastian Ullrich
                                    <br />
                                    Kronenstr. 18
                                    <br />
                                    10117 Berlin
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Disclaimer */}
                    <Box sx={{ mt: 6, pt: 4, borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.primary.main} 90%)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                }}
                            >
                                <Info />
                            </Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                Disclaimer
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Business sx={{ color: 'primary.main', mt: 0.5 }} />
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                        Haftung für Inhalte
                                    </Typography>
                                    <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                        Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen
                                        Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind
                                        wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder
                                        gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen,
                                        die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung
                                        oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen
                                        bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem
                                        Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei
                                        Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte
                                        umgehend entfernen.
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Email sx={{ color: 'primary.main', mt: 0.5 }} />
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                        Haftung für Links
                                    </Typography>
                                    <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                        Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir
                                        keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine
                                        Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige
                                        Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden
                                        zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft.
                                        Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine
                                        permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete
                                        Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von
                                        Rechtsverletzungen werden wir derartige Links umgehend entfernen.
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <LocationOn sx={{ color: 'primary.main', mt: 0.5 }} />
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                        Urheberrecht
                                    </Typography>
                                    <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                        Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten
                                        unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung,
                                        Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes
                                        bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
                                        Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen
                                        Gebrauch gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber
                                        erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden
                                        Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine
                                        Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden
                                        Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte
                                        umgehend entfernen.
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Content>
    );
}

export default Impressum;
